import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Switch, Space, Tag, Popconfirm,
  message, Typography, Tabs, Drawer, Statistic, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  BarChartOutlined, CopyOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { landingPageAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function LandingPageAdminPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [editorDrawer, setEditorDrawer] = useState(false);
  const [editorPage, setEditorPage] = useState(null);
  const [statsModal, setStatsModal] = useState(null);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [form] = Form.useForm();
  const [editorForm] = Form.useForm();

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await landingPageAPI.list();
      setPages(res.data);
    } catch (e) {
      message.error('載入失敗: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleCreate = () => {
    setEditingPage(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingPage(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingPage) {
        await landingPageAPI.update(editingPage.id, values);
        message.success('已更新');
      } else {
        await landingPageAPI.create(values);
        message.success('已建立');
      }
      setModalOpen(false);
      fetchPages();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await landingPageAPI.delete(id);
      message.success('已刪除');
      fetchPages();
    } catch (e) {
      message.error('刪除失敗: ' + e.message);
    }
  };

  const openEditor = (record) => {
    setEditorPage(record);
    editorForm.setFieldsValue({
      html_content: record.html_content || '',
      css_content: record.css_content || '',
      form_config: record.form_config ? JSON.stringify(record.form_config, null, 2) : '',
      seo_meta: record.seo_meta ? JSON.stringify(record.seo_meta, null, 2) : '',
    });
    setEditorDrawer(true);
  };

  const saveEditor = async () => {
    try {
      const values = await editorForm.validateFields();
      const payload = {
        html_content: values.html_content,
        css_content: values.css_content,
      };
      if (values.form_config) {
        try { payload.form_config = JSON.parse(values.form_config); } catch { message.error('表單設定 JSON 格式錯誤'); return; }
      }
      if (values.seo_meta) {
        try { payload.seo_meta = JSON.parse(values.seo_meta); } catch { message.error('SEO 設定 JSON 格式錯誤'); return; }
      }
      await landingPageAPI.update(editorPage.id, payload);
      message.success('已儲存');
      setEditorDrawer(false);
      fetchPages();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const openStats = async (record) => {
    setStatsModal(record);
    try {
      const [statsRes, subRes] = await Promise.all([
        landingPageAPI.getStats(record.id),
        landingPageAPI.getSubmissions(record.id),
      ]);
      setStats(statsRes.data);
      setSubmissions(subRes.data);
    } catch (e) {
      message.error('載入統計失敗');
    }
  };

  const copyPublicUrl = (slug) => {
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(url);
    message.success('已複製公開連結');
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '標題', dataIndex: 'title', ellipsis: true },
    {
      title: 'Slug', dataIndex: 'slug',
      render: (slug) => (
        <Space>
          <Text code>{slug}</Text>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyPublicUrl(slug)} />
        </Space>
      ),
    },
    {
      title: '狀態', dataIndex: 'is_published', width: 80,
      render: (v) => v ? <Tag color="green">已發佈</Tag> : <Tag>草稿</Tag>,
    },
    { title: '瀏覽數', dataIndex: 'visit_count', width: 80 },
    { title: '提交數', dataIndex: 'submission_count', width: 80 },
    {
      title: '操作', width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>設定</Button>
          <Button size="small" icon={<GlobalOutlined />} onClick={() => openEditor(record)}>編輯</Button>
          <Button size="small" icon={<BarChartOutlined />} onClick={() => openStats(record)}>統計</Button>
          <Popconfirm title="確認刪除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Landing Page 管理</Title>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增頁面</Button>}
      >
        <Table
          columns={columns}
          dataSource={pages}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="middle"
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingPage ? '編輯頁面設定' : '新增 Landing Page'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="標題" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="slug" label="Slug (URL 路徑)" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '只允許小寫字母、數字、連字號' }]}>
            <Input addonBefore={`${window.location.origin}/lp/`} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="scenario_id" label="關聯情境 ID">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="is_published" label="發佈" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Content Editor Drawer */}
      <Drawer
        title={`編輯內容：${editorPage?.title || ''}`}
        open={editorDrawer}
        onClose={() => setEditorDrawer(false)}
        width={900}
        extra={<Button type="primary" onClick={saveEditor}>儲存</Button>}
      >
        <Form form={editorForm} layout="vertical">
          <Tabs items={[
            {
              key: 'html', label: 'HTML 內容',
              children: (
                <Form.Item name="html_content">
                  <TextArea rows={20} style={{ fontFamily: 'monospace', fontSize: 13 }} />
                </Form.Item>
              ),
            },
            {
              key: 'css', label: 'CSS 樣式',
              children: (
                <Form.Item name="css_content">
                  <TextArea rows={20} style={{ fontFamily: 'monospace', fontSize: 13 }} />
                </Form.Item>
              ),
            },
            {
              key: 'form', label: '表單設定 (JSON)',
              children: (
                <Form.Item name="form_config">
                  <TextArea rows={15} style={{ fontFamily: 'monospace', fontSize: 13 }} placeholder='[{"name":"email","label":"Email","type":"email","required":true}]' />
                </Form.Item>
              ),
            },
            {
              key: 'seo', label: 'SEO 設定 (JSON)',
              children: (
                <Form.Item name="seo_meta">
                  <TextArea rows={10} style={{ fontFamily: 'monospace', fontSize: 13 }} placeholder='{"title":"Page Title","description":"...","og_image":""}' />
                </Form.Item>
              ),
            },
            {
              key: 'preview', label: '預覽',
              children: editorPage && (
                <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                  <iframe
                    title="preview"
                    srcDoc={`<style>${editorForm.getFieldValue('css_content') || ''}</style>${editorForm.getFieldValue('html_content') || ''}`}
                    style={{ width: '100%', height: 500, border: 'none' }}
                  />
                </div>
              ),
            },
          ]} />
        </Form>
      </Drawer>

      {/* Stats Modal */}
      <Modal
        title={`統計：${statsModal?.title || ''}`}
        open={!!statsModal}
        onCancel={() => { setStatsModal(null); setStats(null); setSubmissions([]); }}
        footer={null}
        width={700}
      >
        {stats && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}><Statistic title="總瀏覽" value={stats.total_visits} /></Col>
              <Col span={6}><Statistic title="獨立訪客" value={stats.unique_visitors} /></Col>
              <Col span={6}><Statistic title="表單提交" value={stats.total_submissions} /></Col>
              <Col span={6}><Statistic title="轉換率" value={stats.conversion_rate} suffix="%" /></Col>
            </Row>
            <Title level={5}>最近提交</Title>
            <Table
              dataSource={submissions}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                {
                  title: '表單資料', dataIndex: 'form_data',
                  render: (v) => <Text style={{ fontSize: 12 }}>{JSON.stringify(v)}</Text>,
                },
                { title: 'IP', dataIndex: 'ip_address', width: 120 },
                {
                  title: '時間', dataIndex: 'created_at', width: 160,
                  render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
